import {offlineIdentity,cacheRead,readCached,acceptLogin} from "../offline/cache";
import {fetchResponse} from "./network";
import type {
  ChapterDetail,
  ChapterDirectoryPage,
  ChapterSummary,
  CreateChapterRequestInput,
  CreateVolumeRequestInput,
  CreateWorkRequestInput,
  CreateWorkResponse,
  ErrorEnvelope,
  HealthReady,
  PutCursorRequest,
  PutWorkPreferencesRequest,
  RegisterResponse,
  RestoreVersionRequest,
  SaveChapterRequest,
  SaveChapterResponse,
  SearchHit,
  SessionResponse,
  UpdateVolumeRequest,
  UpdateWorkRequest,
  VersionSummary,
  Volume,
  WorkDetail,
  WorkListPage,
  WorkPreferences,
  WorkSummary,
  WritingCursor,
  StoryCatalog
} from "@novel/contracts";
import type { AiCheckView, AiTaskSnapshot, CreateAiTaskRequest, BookPlan, BookPlanData, CategoryRecord, CharacterStageRecord, ForeshadowRecord, ForeshadowTransition, ReferenceTerm, SaveStoryRecord, SourceAnchor, SourceLocation, StoryKind, StoryLink, StoryRecord, StoryRecordWorkspace, StorySummary, StoryVersion, SummaryJob, ChatSession, ChatMessage, ChatProposal, ChatDecisionSummary, SendChatMessageRequest, CreateChatBranchRequest } from "@novel/contracts";

/**
 * 前端唯一的后端访问入口。业务组件不直接调用 fetch，
 * 以便统一处理错误信封、CSRF 头与凭据。
 */
export class ApiError extends Error {
  readonly code: string;
  readonly requestId: string;
  readonly status: number;
  readonly details: unknown;

  constructor(options: {
    code: string;
    message: string;
    requestId: string;
    status: number;
    details?: unknown;
  }) {
    super(options.message);
    this.name = "ApiError";
    this.code = options.code;
    this.requestId = options.requestId;
    this.status = options.status;
    this.details = options.details;
  }
}

const CSRF_COOKIE = "novel_csrf";
const CSRF_HEADER = "x-novel-csrf";
const WRITE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export const getChapterSource=(workId:string,id:string,revision:number)=>request<{source:import("@novel/contracts").ChapterSource}>("/api/works/"+workId+"/chapters/"+id+"/source/"+revision);
function planPath(workId:string,kind:import("@novel/contracts").PlanKind,id:string){return "/api/works/"+workId+(kind==="book_plan"?"/book-plan":"/volumes/"+id+"/plan");}
export const getPlanSource=(workId:string,kind:import("@novel/contracts").PlanKind,id:string,revision:number)=>request<{source:{revision:number;value:import("@novel/contracts").PlanValue}}>(planPath(workId,kind,id)+"/source/"+revision);
export const resolvePlan=(workId:string,kind:import("@novel/contracts").PlanKind,id:string,body:import("@novel/contracts").ResolvePlanRequest)=>request<{revision:number;value:import("@novel/contracts").PlanValue}>(planPath(workId,kind,id)+"/merge",{method:"POST",body});
export async function readPlanHead(workId:string,kind:import("@novel/contracts").PlanKind,id:string):Promise<{revision:number;value:import("@novel/contracts").PlanValue}>{
 if(kind==="book_plan"){const {plan}=await getBookPlan(workId);return {revision:plan.revision,value:{title:"",data:plan.data}};}
 const volume=(await listVolumes(workId)).items.find(item=>item.id===id);if(!volume)throw new ApiError({status:404,code:"not_found",message:"此卷已移除，本机稿件仍保留。",requestId:"plan-head"});
 return {revision:volume.revision,value:{title:volume.title,data:volume.direction}};
}
export const resolveChapter=(workId:string,id:string,body:import("@novel/contracts").ResolveChapterRequest)=>request<{revision:number}>("/api/works/"+workId+"/chapters/"+id+"/merge",{method:"POST",body});
export const exportWriting=(id:string,body:import("@novel/contracts").ExportWritingRequest)=>request<import("@novel/contracts").ExportedWriting>("/api/works/"+id+"/export",{method:"POST",body});
export const publishWriting=(id:string,body:import("@novel/contracts").PublishWritingRequest)=>request<{publication:import("@novel/contracts").Publication}>("/api/works/"+id+"/publications",{method:"POST",body});
export const getPublications=(id:string)=>request<{items:import("@novel/contracts").Publication[]}>("/api/works/"+id+"/publications");
export const getWritingStatistics=(id:string)=>request<import("@novel/contracts").WritingStatistics>("/api/works/"+id+"/statistics");
export const getWritingManagement=(workId:string)=>request<import("@novel/contracts").WritingManagementView>("/api/works/"+workId+"/manage");
export const moveManagedChapter=(workId:string,id:string,body:{mutationId:string;expectedRevision:number;volumeId:string|null})=>request<{revision:number}>("/api/works/"+workId+"/chapters/"+id+"/move",{method:"POST",body});
export const trashManagedWriting=(workId:string,kind:"chapter"|"volume",id:string,body:{mutationId:string;expectedRevision:number;deleted:boolean})=>request<{revision:number}>("/api/works/"+workId+"/"+(kind==="chapter"?"chapters":"volumes")+"/"+id+"/trash-state",{method:"POST",body});
export const getTrashedWorks=()=>request<{items:{id:string;title:string;revision:number}[]}>("/api/works/trashed");
export const workAction=(id:string,action:"trash"|"untrash"|"archive"|"unarchive")=>request<{work:WorkSummary}>("/api/works/"+id+"/"+action,{method:"POST"});
export const listInvites=()=>request<{items:import("@novel/contracts").AdminInvite[]}>("/api/admin/invites");
export const createInviteBatch=(body:{mutationId:string;count:number;maxUses:number;expiresInDays:number|null;note:string})=>request<{items:{id:string;code?:string}[];replayed:boolean}>("/api/admin/invites/batch",{method:"POST",body});
export const revokeInvite=(id:string,mutationId:string)=>request<{revoked:boolean}>("/api/admin/invites/"+id+"/revoke",{method:"POST",body:{mutationId}});
export const setAccountStatus=(id:string,body:{mutationId:string;status:"active"|"disabled"})=>request<{updated:boolean}>("/api/admin/users/"+id+"/status",{method:"POST",body});
export const getBackups=(w:string)=>request<{items:{id:string;bytes:number;createdAt:string;automatic:boolean}[]}>("/api/works/"+w+"/backups");
export const createBackup=(w:string,mutationId:string)=>request<{id:string;bytes:number;createdAt:string}>("/api/works/"+w+"/backups",{method:"POST",body:{mutationId}});
export const downloadBackup=(w:string,id:string)=>requestBinary("/api/works/"+w+"/backups/"+id);
export const inspectBackup=(bytes:Uint8Array)=>request<{title:string;createdAt:string;chapterCount:number;schemaVersion:string}>("/api/backups/inspect",{method:"POST",rawBody:bytes});
export const restoreBackup=(mutationId:string,bytes:Uint8Array)=>request<{workId:string;replayed:boolean}>("/api/backups/restore?mutationId="+mutationId,{method:"POST",rawBody:bytes});
export const getCover=(w:string)=>request<import("@novel/contracts").CoverView>("/api/works/"+w+"/cover");
export const uploadCover=(w:string,mutationId:string,expectedRevision:number,bytes:Uint8Array)=>request<{cover:import("@novel/contracts").Cover}>("/api/works/"+w+"/cover?mutationId="+mutationId+"&expectedRevision="+expectedRevision,{method:"POST",rawBody:bytes});
export const adoptCover=(w:string,body:{mutationId:string;expectedRevision:number;coverId:string|null})=>request<import("@novel/contracts").CoverView>("/api/works/"+w+"/cover/adopt",{method:"POST",body});
export const generateCover=(w:string,body:{mutationId:string;expectedRevision:number;prompt:string})=>request<{job:import("@novel/contracts").CoverGenerationJob}>("/api/works/"+w+"/cover/generate",{method:"POST",body});
export const getCoverJob=(w:string,id:string)=>request<{job:import("@novel/contracts").CoverGenerationJob}>("/api/works/"+w+"/cover/jobs/"+id);
export const stopCoverJob=(w:string,id:string)=>request<{job:import("@novel/contracts").CoverGenerationJob}>("/api/works/"+w+"/cover/jobs/"+id+"/stop",{method:"POST"});
export const getImageQuota=(userId?:string)=>request<import("@novel/contracts").QuotaView>(userId?"/api/admin/users/"+userId+"/image-quota":"/api/image-usage");
export const getImageQuotaCalls=(userId?:string)=>request<{items:import("@novel/contracts").ImageQuotaCall[]}>(userId?"/api/admin/users/"+userId+"/image-usage":"/api/image-usage/calls");
export const adjustImageQuota=(id:string,body:import("zod").infer<typeof import("@novel/contracts").adjustQuotaSchema>)=>request<import("@novel/contracts").QuotaView>("/api/admin/users/"+id+"/image-quota",{method:"PUT",body});
export const reconcileImageQuota=(id:string,body:{mutationId:string;units:number;reason:string})=>request<{ok:true}>("/api/admin/image-usage/"+id+"/reconcile",{method:"POST",body});
export const getQuota=()=>request<import("@novel/contracts").QuotaView>("/api/usage");
export const retryAutomaticSummary=(workId:string,id:string)=>request<{ok:true}>(`/api/works/${workId}/story/summary-jobs/${id}/retry`,{method:"POST"});
export const getQuotaCalls=()=>request<{items:import("@novel/contracts").QuotaCall[]}>("/api/usage/calls");
export const listAdminUsers=()=>request<{items:{id:string;email:string;role:"user"|"admin";status:string}[]}>("/api/admin/users");
export const getUserQuota=(id:string)=>request<import("@novel/contracts").QuotaView>(`/api/admin/users/${id}/quota`);
export const getUserQuotaCalls=(id:string)=>request<{items:import("@novel/contracts").QuotaCall[]}>(`/api/admin/users/${id}/usage`);
export const adjustUserQuota=(id:string,body:{mutationId:string;expectedRevision:number;dailyLimit:number})=>request<import("@novel/contracts").QuotaView>(`/api/admin/users/${id}/quota`,{method:"PUT",body});
export const reconcileQuotaCall=(id:string,body:{mutationId:string;inputTokens:number;outputTokens:number;reason:string})=>request<{ok:true}>(`/api/admin/usage/${id}/reconcile`,{method:"POST",body});
export const listAdminAiTasks=(state?:string)=>request<{items:import("@novel/contracts").AiAdminTaskSummary[]}>(`/api/admin/ai/tasks${state?`?state=${encodeURIComponent(state)}`:""}`);
export const stopAdminAiTask=(id:string)=>request<{ok:true}>(`/api/admin/ai/tasks/${id}/stop`,{method:"POST"});
export const retryChatSummary=(id:string)=>request<{ok:true}>(`/api/ai-chat/summary-jobs/${id}/retry`,{method:"POST"});
export const getChatDecisions=(sessionId:string)=>request<{summaryJobs:import("@novel/contracts").ChatSummaryJob[];summaries:import("@novel/contracts").ChatDecisionSummary[]}>(`/api/ai-chat/sessions/${sessionId}/decisions`);

/** 双重提交：令牌同时存在于可读 Cookie 与请求头里，由服务端比对。 */
function csrfToken(): string | undefined {
  const match = new RegExp("(?:^|;\\s*)" + CSRF_COOKIE + "=([^;]+)").exec(document.cookie);
  return match?.[1] ? decodeURIComponent(match[1]) : undefined;
}

async function requestBinary(path: string, options: { method?: string; signal?: AbortSignal } = {}): Promise<Uint8Array> {
  const owner=offlineIdentity();
  const method = options.method ?? "GET";
  const headers: Record<string, string> = {};
  if (WRITE_METHODS.has(method)) {
    const token = csrfToken();
    if (token) headers[CSRF_HEADER] = token;
  }
  const response = await fetchResponse(path, { method, headers, credentials: "same-origin", ...(options.signal ? { signal: options.signal } : {}) });
  assertRequestOwner(path,owner?.id);
  if (!response.ok) {
    let envelope: ErrorEnvelope | undefined;
    try { envelope = (await response.json()) as ErrorEnvelope; } catch { /* binary endpoint may return a proxy error body */ }
    throw new ApiError({ code: envelope?.error?.code ?? "internal_error", message: envelope?.error?.message ?? "请求失败（" + response.status + "）", requestId: envelope?.error?.requestId ?? "unknown", status: response.status, details: envelope?.error?.details });
  }
  const bytes=new Uint8Array(await response.arrayBuffer());
  assertRequestOwner(path,owner?.id);return bytes;
}
function assertRequestOwner(path:string,ownerId:string|undefined){
  if(ownerId&&!path.startsWith("/api/auth/")&&ownerId!==offlineIdentity()?.id)throw new ApiError({code:"unauthorized",status:401,message:"账号已切换，已忽略上一次会话的返回内容。",requestId:"local-session-change"});
}
async function request<T>(path: string, options: { method?: string; body?: unknown;rawBody?:Uint8Array; cache?:RequestCache;signal?:AbortSignal;persistCache?:"await"|"background" } = {}): Promise<T> {
  const method = options.method ?? "GET";
  // 只有真的带请求体时才声明 content-type。
  // 否则像"登出"这种无请求体的 POST 会因为 application/json + 空 body
  // 被 Fastify 直接判为 400，请求根本到不了处理逻辑。
  const headers: Record<string, string> = {};
  if(options.rawBody)headers["content-type"]="application/octet-stream";
  if (options.body !== undefined) {
    headers["content-type"] = "application/json";
  }
  if (WRITE_METHODS.has(method)) {
    const token = csrfToken();
    if (token) headers[CSRF_HEADER] = token;
  }

  const owner=offlineIdentity();
  const assertOwner=()=>assertRequestOwner(path,owner?.id);
  let response:Response;
  try{response = await fetchResponse(path, {
    method,
    headers,
    credentials: "same-origin",
    ...(options.cache?{cache:options.cache}:{}),
    ...(options.signal?{signal:options.signal}:{}),
    ...(options.rawBody?{body:new Blob([options.rawBody as Uint8Array<ArrayBuffer>])}:options.body === undefined ? {} : { body: JSON.stringify(options.body) })
  });}catch(error){
    assertOwner();
    if(!options.signal?.aborted&&method==="GET"&&owner){const saved=await readCached<T>(owner.id,path);assertOwner();if(saved!==undefined)return saved;}
    throw error;
  }

  assertOwner();

  if(!response.ok&&[502,503,504].includes(response.status)&&method==="GET"&&owner){const saved=await readCached<T>(owner.id,path);assertOwner();if(saved!==undefined)return saved;}
  if (!response.ok) {
    let envelope: ErrorEnvelope | undefined;
    try {
      envelope = (await response.json()) as ErrorEnvelope;
    } catch {
      // 非 JSON 错误体（例如反向代理返回 HTML）也要归一到 ApiError。
    }
    throw new ApiError({
      code: envelope?.error?.code ?? "internal_error",
      message: envelope?.error?.message ?? "请求失败（" + response.status + "）",
      requestId: envelope?.error?.requestId ?? "unknown",
      status: response.status,
      details: envelope?.error?.details
    });
  }

  if (response.status === 204) return undefined as T;
  const data=(await response.json()) as T;
  if(method==="GET"&&owner){
    const persistence=cacheRead(owner.id,path,data);
    if(options.persistCache==="background")void persistence;
    else await persistence;
  }
  assertOwner();
  return data;
}

export function fetchHealthReady(): Promise<HealthReady> {
  return request<HealthReady>("/health/ready");
}

export const requestPasswordReset=(email:string)=>request<{accepted:boolean}>("/api/auth/forgot-password",{method:"POST",body:{email}});
export const resetPassword=(token:string,password:string)=>request<{reset:boolean}>("/api/auth/reset-password",{method:"POST",body:{token,password}});
export function getSession(signal?:AbortSignal): Promise<SessionResponse> {
  return request<SessionResponse>("/api/auth/session",{...(signal?{signal}:{})});
}

export async function login(identifier: string, password: string): Promise<SessionResponse> {
  const session=await request<SessionResponse>("/api/auth/login", { method: "POST", body: { identifier, password } });
  await acceptLogin(session);return session;
}

export function logout(): Promise<void> {
  return request<void>("/api/auth/logout", { method: "POST",signal:AbortSignal.timeout(10000) });
}

export function register(email: string, password: string, inviteCode?: string): Promise<RegisterResponse> {
  return request<RegisterResponse>("/api/auth/register", {
    method: "POST",
    body: { email, password, ...(inviteCode ? { inviteCode } : {}) }
  });
}

export function verifyEmail(token: string): Promise<{ account: unknown }> {
  return request<{ account: unknown }>("/api/auth/verify-email", { method: "POST", body: { token } });
}

export function listWorks(includeArchived=false, cursor?: string, limit=50): Promise<WorkListPage> {
  const query = new URLSearchParams({
    includeArchived: String(includeArchived),
    limit: String(limit)
  });
  if (cursor) query.set("cursor", cursor);
  return request<WorkListPage>("/api/works?" + query.toString());
}

export function createWork(command: CreateWorkRequestInput): Promise<CreateWorkResponse> {
  return request<CreateWorkResponse>("/api/works", { method: "POST", body: command });
}

export function getWork(workId: string): Promise<{ work: WorkDetail }> {
  return request<{ work: WorkDetail }>("/api/works/" + workId);
}

export function updateWork(workId: string, command: UpdateWorkRequest): Promise<{ work: WorkSummary }> {
  return request<{ work: WorkSummary }>("/api/works/" + workId, { method: "PATCH", body: command });
}

/* ---------------- 卷 ---------------- */

export function listVolumes(workId: string): Promise<{ items: Volume[] }> {
  return request<{ items: Volume[] }>("/api/works/" + workId + "/volumes");
}

export function createVolume(
  workId: string,
  command: CreateVolumeRequestInput
): Promise<{ volume: Volume }> {
  return request<{ volume: Volume }>("/api/works/" + workId + "/volumes", { method: "POST", body: command });
}

export function updateVolume(
  workId: string,
  volumeId: string,
  command: UpdateVolumeRequest
): Promise<{ volume: Volume }> {
  return request<{ volume: Volume }>("/api/works/" + workId + "/volumes/" + volumeId, {
    method: "PATCH",
    body: command
  });
}

export function deleteVolume(workId: string, volumeId: string): Promise<void> {
  return request<void>("/api/works/" + workId + "/volumes/" + volumeId, { method: "DELETE" });
}

export function reorderVolumes(workId: string, orderedIds: string[],expectedOrderedIds:string[],mutationId:string=crypto.randomUUID()): Promise<{ items: Volume[] }> {
  return request<{ items: Volume[] }>("/api/works/" + workId + "/volumes/reorder", {
    method: "POST",
    body: { mutationId, expectedOrderedIds, orderedIds }
  });
}

/* ---------------- 章节 ---------------- */

export function listChapters(workId: string): Promise<{ items: ChapterSummary[] }> {
  return request<{ items: ChapterSummary[] }>("/api/works/" + workId + "/chapters");
}

export function getChapterDirectory(
  workId: string,
  options: { cursor?: string; anchor?: string; volumeId?: string | null; limit?: number } = {}
): Promise<ChapterDirectoryPage> {
  const query = new URLSearchParams({ limit: String(options.limit ?? 100) });
  if (options.cursor) query.set("cursor", options.cursor);
  if (options.anchor) query.set("anchor", options.anchor);
  if (options.volumeId !== undefined) query.set("volumeId", options.volumeId ?? "unfiled");
  return request<ChapterDirectoryPage>(`/api/works/${workId}/chapter-directory?${query.toString()}`);
}

export function createChapter(
  workId: string,
  command: CreateChapterRequestInput
): Promise<{ chapter: ChapterDetail }> {
  return request<{ chapter: ChapterDetail }>("/api/works/" + workId + "/chapters", {
    method: "POST",
    body: command
  });
}

export function readChapter(workId: string, chapterId: string): Promise<{ chapter: ChapterDetail }> {
  return request<{ chapter: ChapterDetail }>("/api/works/" + workId + "/chapters/" + chapterId,{persistCache:"background"});
}
export function readCachedChapter(workId:string,chapterId:string):Promise<{chapter:ChapterDetail}|undefined>{
  const owner=offlineIdentity();
  if(!owner)return Promise.resolve(undefined);
  return readCached<{chapter:ChapterDetail}>(owner.id,"/api/works/"+workId+"/chapters/"+chapterId);
}

export function saveChapter(
  workId: string,
  chapterId: string,
  command: SaveChapterRequest
): Promise<SaveChapterResponse> {
  return request<SaveChapterResponse>("/api/works/" + workId + "/chapters/" + chapterId, {
    method: "PUT",
    body: command
  });
}

export function deleteChapter(workId: string, chapterId: string): Promise<void> {
  return request<void>("/api/works/" + workId + "/chapters/" + chapterId, { method: "DELETE" });
}

export function reorderChapters(workId: string, orderedIds: string[],expectedOrderedIds:string[],mutationId:string=crypto.randomUUID()): Promise<{ items: ChapterSummary[] }> {
  return request<{ items: ChapterSummary[] }>("/api/works/" + workId + "/chapters/reorder", {
    method: "POST",
    body: { mutationId, expectedOrderedIds, orderedIds }
  });
}

/* ---------------- 查找 ---------------- */

export function searchWorkspace(workId: string, keyword: string): Promise<{ items: SearchHit[] }> {
  return request<{ items: SearchHit[] }>(
    "/api/works/" + workId + "/search?q=" + encodeURIComponent(keyword)
  );
}

/* ---------------- 历史版本 ---------------- */

export function listVersions(workId: string, chapterId: string,beforeRevision?:number): Promise<{ items: VersionSummary[];nextBeforeRevision:number|null }> {
  return request<{ items: VersionSummary[];nextBeforeRevision:number|null }>(
    "/api/works/" + workId + "/chapters/" + chapterId + "/versions"+(beforeRevision===undefined?"":"?beforeRevision="+beforeRevision)
  );
}

export function readVersion(
  workId: string,
  chapterId: string,
  revision: number
): Promise<{ version: { versionNumber:number;revision: number; title: string; doc: Record<string, unknown>;plan:Record<string,unknown>; wordCount: number } }> {
  return request<{ version: { versionNumber:number;revision: number; title: string; doc: Record<string, unknown>;plan:Record<string,unknown>; wordCount: number } }>(
    "/api/works/" + workId + "/chapters/" + chapterId + "/versions/" + revision
  );
}

export function restoreVersion(
  workId: string,
  chapterId: string,
  command: RestoreVersionRequest
): Promise<{ chapter: ChapterDetail }> {
  return request<{ chapter: ChapterDetail }>(
    "/api/works/" + workId + "/chapters/" + chapterId + "/restore-version",
    { method: "POST", body: command }
  );
}

/* ---------------- 光标与偏好 ---------------- */

export function getCursor(workId: string, deviceId: string): Promise<{ cursor: WritingCursor | null }> {
  return request<{ cursor: WritingCursor | null }>(
    "/api/works/" + workId + "/cursor?deviceId=" + encodeURIComponent(deviceId)
  );
}

export function putCursor(workId: string, command: PutCursorRequest): Promise<void> {
  return request<void>("/api/works/" + workId + "/cursor", { method: "PUT", body: command });
}

export function getWorkPreferences(workId: string): Promise<{ preferences: WorkPreferences }> {
  return request<{ preferences: WorkPreferences }>("/api/works/" + workId + "/preferences");
}
export function getEditorPreferences():Promise<{revision:number;data:import("@novel/contracts").EditorPreferences}>{return request("/api/writing/editor-preferences");}
export function saveEditorPreferences(body:{mutationId:string;expectedRevision:number;data:import("@novel/contracts").EditorPreferences}):Promise<{revision:number;data:import("@novel/contracts").EditorPreferences}>{return request("/api/writing/editor-preferences",{method:"PUT",body});}
export function getWritingNotes(workId:string,chapterId:string):Promise<{items:import("@novel/contracts").WritingNote[]}>{return request(`/api/works/${workId}/chapters/${chapterId}/notes`);}
export function saveWritingNote(workId:string,id:string|null,body:{mutationId:string;expectedRevision:number;selection:import("@novel/contracts").SelectedPassage;content:string;resolved:boolean}):Promise<{note:import("@novel/contracts").WritingNote}>{return request(`/api/works/${workId}/notes${id?"/"+id:""}`,{method:id?"PUT":"POST",body});}

export function putWorkPreferences(
  workId: string,
  command: PutWorkPreferencesRequest
): Promise<{ preferences: WorkPreferences }> {
  return request<{ preferences: WorkPreferences }>("/api/works/" + workId + "/preferences", {
    method: "PUT",
    body: command
  });
}

export function initializeStory(workId: string): Promise<{ ready: boolean }> {
  return request<{ ready: boolean }>(`/api/works/${workId}/story/initialize`, { method: "POST", body: {} });
}
export function bootstrapStoryCatalog(workId:string):Promise<StoryCatalog>{
  return request<StoryCatalog>(`/api/works/${workId}/story/catalog`,{method:"POST",body:{}});
}
export function getSummaryJobs(workId: string, chapterId?: string | null): Promise<{ items: SummaryJob[] }> {
  return request<{ items: SummaryJob[] }>(`/api/works/${workId}/story/summary-jobs${chapterId ? `?chapterId=${encodeURIComponent(chapterId)}` : ""}`);
}
export function getStoryCategories(workId: string): Promise<{ items: CategoryRecord[] }> {
  return request<{ items: CategoryRecord[] }>(`/api/works/${workId}/story/categories`,{cache:"no-store"});
}
export function getStoryTerms(workId: string): Promise<{ items: ReferenceTerm[] }> {
  return request<{ items: ReferenceTerm[] }>(`/api/works/${workId}/story/terms`,{cache:"no-store"});
}
export function listStoryRecords(workId: string, kind: StoryKind, after = 0, deleted = false): Promise<{ items: StorySummary[]; nextAfter: number | null }> {
  return request<{ items: StorySummary[]; nextAfter: number | null }>(`/api/works/${workId}/story/records?kind=${encodeURIComponent(kind)}&after=${after}&deleted=${deleted}`,{cache:"no-store"});
}
export function getStoryRecord(workId: string, id: string, signal?:AbortSignal): Promise<{ record: StoryRecord }> {
  return request<{ record: StoryRecord }>(`/api/works/${workId}/story/records/${id}`,{cache:"no-store",...(signal?{signal}:{})});
}
export function getStoryRecordWorkspace(workId:string,id:string,signal?:AbortSignal):Promise<StoryRecordWorkspace>{
  return request<StoryRecordWorkspace>(`/api/works/${workId}/story/records/${id}/workspace`,{cache:"no-store",...(signal?{signal}:{})});
}
export function createStoryRecord(workId: string, body: SaveStoryRecord): Promise<{ record: StoryRecord }> {
  return request<{ record: StoryRecord }>(`/api/works/${workId}/story/records`, { method: "POST", body });
}
export function updateStoryRecord(workId: string, id: string, body: SaveStoryRecord): Promise<{ record: StoryRecord }> {
  return request<{ record: StoryRecord }>(`/api/works/${workId}/story/records/${id}`, { method: "PUT", body });
}
export function storyRecordVersions(workId: string, id: string, before = 2147483647): Promise<{ items: StoryVersion[] }> {
  return request<{ items: StoryVersion[] }>(`/api/works/${workId}/story/records/${id}/versions?before=${before}`);
}
export function storyRecordAction(workId: string, id: string, action: "trash" | "untrash" | "restore", body: { mutationId: string; expectedRevision: number; revision?: number;cascade?:boolean;children?:{id:string;revision:number}[] }): Promise<{ record: StoryRecord }> {
  return request<{ record: StoryRecord }>(`/api/works/${workId}/story/records/${id}/${action}`, { method: "POST", body });
}
export function transitionForeshadow(workId: string, id: string, body: ForeshadowTransition): Promise<{ record: ForeshadowRecord }> {
  return request<{ record: ForeshadowRecord }>(`/api/works/${workId}/story/records/${id}/transition`, { method: "POST", body });
}
export function storyLinks(workId: string, id: string): Promise<{ items: StoryLink[] }> {
  return request<{ items: StoryLink[] }>(`/api/works/${workId}/story/records/${id}/links`);
}
export function discoverStoryLinks(workId: string, id: string, body: { mutationId: string; chapterId?: string | null; after?: number; matchAfter?: number }): Promise<{ count: number; nextAfter: number | null; nextMatchAfter: number }> {
  return request<{ count: number; nextAfter: number | null; nextMatchAfter: number }>(`/api/works/${workId}/story/records/${id}/discover-links`, { method: "POST", body });
}
export function createStoryLink(workId: string, body: { mutationId: string; referenceId: string; anchor: SourceAnchor }): Promise<{ link: StoryLink }> {
  return request<{ link: StoryLink }>(`/api/works/${workId}/story/links`, { method: "POST", body });
}
export function decideStoryLink(workId: string, id: string, body: { mutationId: string; expectedRevision: number; state: "proposed" | "confirmed" | "ignored" }): Promise<{ link: StoryLink }> {
  return request<{ link: StoryLink }>(`/api/works/${workId}/story/links/${id}/decision`, { method: "POST", body });
}
export function locateStorySource(workId: string, anchor: SourceAnchor): Promise<SourceLocation> {
  return request<SourceLocation>(`/api/works/${workId}/story/sources/locate`, { method: "POST", body: anchor });
}
export function storyContext(workId: string, chapterId: string): Promise<{ storyOrder: number | null; due: ForeshadowRecord[]; stages: CharacterStageRecord[] }> {
  return request<{ storyOrder: number | null; due: ForeshadowRecord[]; stages: CharacterStageRecord[] }>(`/api/works/${workId}/story/context/${chapterId}`);
}
export function getBookPlan(workId: string): Promise<{ plan: BookPlan }> {
  return request<{ plan: BookPlan }>(`/api/works/${workId}/book-plan`);
}
export function saveBookPlan(workId: string, body: { mutationId: string; expectedRevision: number; data: BookPlanData }): Promise<{ plan: BookPlan }> {
  return request<{ plan: BookPlan }>(`/api/works/${workId}/book-plan`, { method: "PUT", body });
}

/* ---------- 人物关系图谱与故事地图 ---------- */

export function getRelationshipGraph(workId:string,options:{deviceId:string;chapterId:string|null;eventIndex?:number|null;view:"chapter"|"author";includePlans:boolean}):Promise<import("@novel/contracts").RelationshipGraphView>{
  const query=new URLSearchParams({deviceId:options.deviceId,view:options.view,includePlans:String(options.includePlans)});
  if(options.chapterId)query.set("chapterId",options.chapterId);
  if(options.eventIndex!==undefined&&options.eventIndex!==null)query.set("eventIndex",String(options.eventIndex));
  return request(`/api/works/${workId}/world/graph?${query}` ,{cache:"no-store"});
}
export function saveRelationshipGraphPosition(workId:string,nodeId:string,body:import("@novel/contracts").SaveGraphPositionRequest){
  return request<{position:{referenceId:string;revision:number;x:number;y:number}}>(`/api/works/${workId}/world/graph/nodes/${nodeId}/position`,{method:"PUT",body});
}
export function saveWorldPreferences(workId:string,body:import("@novel/contracts").SaveWorldPreferencesRequest){
  return request<{preferences:import("@novel/contracts").WorldPreferences}>(`/api/works/${workId}/world/preferences`,{method:"PUT",body});
}
export function setWorldObjectLock(workId:string,body:import("@novel/contracts").SetWorldLockRequest){
  return request<{lock:import("@novel/contracts").WorldLock}>(`/api/works/${workId}/world/locks`,{method:"PUT",body});
}
export function decideWorldCandidate(workId:string,id:string,body:import("@novel/contracts").DecideWorldCandidateRequest){
  return request<{candidate:import("@novel/contracts").WorldCandidate}>(`/api/works/${workId}/world/candidates/${id}/decision`,{method:"POST",body});
}
export function decideWorldCandidateBatch(workId:string,body:import("@novel/contracts").DecideWorldCandidateBatchRequest){
  return request<{batch:import("@novel/contracts").WorldDecisionBatch;candidates:import("@novel/contracts").WorldCandidate[]}>(`/api/works/${workId}/world/candidate-batches`,{method:"POST",body});
}
export function undoWorldCandidateBatch(workId:string,id:string,body:import("@novel/contracts").UndoWorldDecisionBatchRequest){
  return request<{batch:import("@novel/contracts").WorldDecisionBatch;candidates:import("@novel/contracts").WorldCandidate[]}>(`/api/works/${workId}/world/candidate-batches/${id}/undo`,{method:"POST",body});
}
export function getWorldMap(workId:string,deviceId:string,zoom:number):Promise<import("@novel/contracts").WorldMapView>{
  const query=new URLSearchParams({deviceId,zoom:String(zoom)});
  return request(`/api/works/${workId}/world/map?${query}`,{cache:"no-store"});
}
export function getAllWorldMapFeatures(workId:string){
  return request<{items:import("@novel/contracts").WorldMapFeature[]}>(`/api/works/${workId}/world/map/features`,{cache:"no-store"});
}
export function createWorldMap(workId:string,body:import("@novel/contracts").CreateWorldMapRequest){
  return request<{map:import("@novel/contracts").WorldMap}>(`/api/works/${workId}/world/map`,{method:"POST",body});
}
export function updateWorldMap(workId:string,body:import("@novel/contracts").UpdateWorldMapRequest){
  return request<{map:import("@novel/contracts").WorldMap}>(`/api/works/${workId}/world/map`,{method:"PUT",body});
}
export function createWorldMapFeature(workId:string,body:import("@novel/contracts").SaveWorldMapFeatureRequest){
  return request<{feature:import("@novel/contracts").WorldMapFeature}>(`/api/works/${workId}/world/map/features`,{method:"POST",body});
}
export function updateWorldMapFeature(workId:string,id:string,body:import("@novel/contracts").SaveWorldMapFeatureRequest){
  return request<{feature:import("@novel/contracts").WorldMapFeature}>(`/api/works/${workId}/world/map/features/${id}`,{method:"PUT",body});
}
export function deleteWorldMapFeature(workId:string,id:string,body:import("@novel/contracts").DeleteWorldMapFeatureRequest){
  return request<{feature:import("@novel/contracts").WorldMapFeature}>(`/api/works/${workId}/world/map/features/${id}`,{method:"DELETE",body});
}

/* ---------- AI 任务（S5） ---------- */

export function createAiTask(workId: string, body: CreateAiTaskRequest): Promise<{ taskId: string; state: string; eventsUrl: string; lastSeq: number }> {
  return request(`/api/works/${workId}/ai/tasks`, { method: "POST", body });
}
export function getAiTaskSnapshot(taskId: string): Promise<AiTaskSnapshot> {
  return request<AiTaskSnapshot>(`/api/ai/tasks/${taskId}/snapshot`);
}
export function stopAiTask(taskId: string): Promise<{ state: string; cancelRequested: boolean }> {
  return request(`/api/ai/tasks/${taskId}/stop`, { method: "POST", body: {} });
}
export function resumeAiTask(taskId: string): Promise<{ taskId: string; state: string; resumed: boolean; referenceManifestHash: string }> {
  return request(`/api/ai/tasks/${taskId}/resume`, { method: "POST", body: { mutationId: crypto.randomUUID() } });
}
export function editAiDraft(draftId: string, body: { mutationId: string; expectedRevision: number; text: string }): Promise<{ draftId: string; revision: number }> {
  return request(`/api/ai/drafts/${draftId}`, { method: "PATCH", body });
}
export function adoptAiDraft(draftId: string, body: { mutationId: string; acknowledgeIncomplete?: boolean;strategy?:import("@novel/contracts").AdoptDraftRequest["strategy"];sourceSelection?:import("@novel/contracts").SelectedPassage;cursorPosition?:number }): Promise<{ adopted: boolean; chapterRevision: number; replayed: boolean }> {
  return request(`/api/ai/drafts/${draftId}/adopt`, { method: "POST", body });
}
export function rejectAiDraft(draftId:string,body:{mutationId:string;expectedRevision:number}):Promise<{rejected:boolean;replayed:boolean}>{
  return request(`/api/ai/drafts/${draftId}/reject`,{method:"POST",body});
}
export function getLatestAiTasks(workId: string, chapterId: string,legacyOnly=false): Promise<{ generationTaskId: string | null; checkTaskId: string | null }> {
  return request(`/api/works/${workId}/ai/tasks/latest?chapterId=${chapterId}&legacyOnly=${legacyOnly}`);
}
export function getAiCheck(taskId: string,draftId?:string): Promise<{ check: AiCheckView | null }> {
  return request<{ check: AiCheckView | null }>(`/api/ai/tasks/${taskId}/check${draftId?"?draftId="+draftId:""}`);
}
export function decideAiIssue(issueId: string, body: { mutationId: string; decision: string; note?: string }): Promise<{ issueId: string; decision: string }> {
  return request(`/api/ai/issues/${issueId}/decision`, { method: "POST", body });
}
export function recheckAiTask(taskId: string, body: { mutationId: string; draftId?: string }): Promise<{ checkTaskId: string }> {
  return request(`/api/ai/tasks/${taskId}/recheck`, { method: "POST", body });
}

export function getAiChatSession(workId: string): Promise<{ session: ChatSession }> { return request(`/api/works/${workId}/ai-chat/session`); }
export function getAiChat(sessionId: string): Promise<import("@novel/contracts").ChatSessionView> { return request(`/api/ai-chat/sessions/${sessionId}`); }
export function clearAiChatHistory(sessionId:string,body:import("@novel/contracts").ClearChatHistoryRequest):Promise<{session:ChatSession}>{return request(`/api/ai-chat/sessions/${sessionId}/clear`,{method:"POST",body});}
export function listAiChatSessions(workId:string):Promise<{mainSessionId:string;sessions:ChatSession[]}>{return request(`/api/works/${workId}/ai-chat/sessions`);}
export function searchChatMentions(workId:string,query:string):Promise<{items:import("@novel/contracts").ChatMention[]}>{return request(`/api/works/${workId}/ai-chat/mentions?q=${encodeURIComponent(query)}`);}
export function getChatDraft(id:string):Promise<import("@novel/contracts").ChatDraftArtifact>{return request(`/api/ai-chat/drafts/${id}`);}
export function stopChatDraft(id:string):Promise<{stopped:boolean}>{return request(`/api/ai-chat/drafts/${id}/stop`,{method:"POST",body:{}});}
export function getDraftVersions(id:string):Promise<{versions:{revision:number;text:string;createdAt:string}[]}>{return request(`/api/ai/drafts/${id}/versions`);}
export function sendAiChatMessage(sessionId:string,body:SendChatMessageRequest):Promise<{run:import("@novel/contracts").ChatRun;eventsUrl:string}>{return request(`/api/ai-chat/sessions/${sessionId}/messages`,{method:"POST",body});}
export function getAiChatRun(runId:string):Promise<import("@novel/contracts").ChatRunSnapshot>{return request(`/api/ai-chat/runs/${runId}`);}
export function stopAiChatRun(runId:string):Promise<{run:import("@novel/contracts").ChatRun}>{return request(`/api/ai-chat/runs/${runId}/stop`,{method:"POST",body:{}});}
export function retryAiChatRun(runId:string,mutationId:string):Promise<{run:import("@novel/contracts").ChatRun}>{return request(`/api/ai-chat/runs/${runId}/retry`,{method:"POST",body:{mutationId}});}
export function getChatContext(sessionId:string,chapterId:string,query:string,refs:string[]=[]):Promise<import("@novel/contracts").MemoryBundle>{return request(`/api/ai-chat/sessions/${sessionId}/context?chapterId=${chapterId}&q=${encodeURIComponent(query)}&refs=${refs.join(",")}`);}
export function getMemoryBundle(workId:string,chapterId:string,query="继续写作"):Promise<import("@novel/contracts").MemoryBundle>{return request(`/api/works/${workId}/memory/bundle/${chapterId}?q=${encodeURIComponent(query)}`);}
export function getAiTaskReferences(taskId:string):Promise<{memory:import("@novel/contracts").MemoryBundle|null;manifestHash:string;warnings:string[]}>{return request(`/api/ai/tasks/${taskId}/references`);}
export function createAiChatBranch(sessionId: string, body: CreateChatBranchRequest): Promise<{ session: ChatSession }> { return request(`/api/ai-chat/sessions/${sessionId}/branches`, { method: "POST", body }); }
export function decideAiChatProposal(proposalId: string, decision: "execute" | "ignore",mutationId=crypto.randomUUID()): Promise<{ proposal: ChatProposal }> { return request(`/api/ai-chat/proposals/${proposalId}/decision`, { method: "POST", body: { mutationId, decision } }); }
export function pinAiChatSummary(summaryId: string, pinned: boolean): Promise<{ summary: ChatDecisionSummary }> { return request(`/api/ai-chat/summaries/${summaryId}/pin`, { method: "POST", body: { pinned } }); }
export function pinAiChatMessage(sessionId:string,messageId:string):Promise<{summary:ChatDecisionSummary}>{return request(`/api/ai-chat/sessions/${sessionId}/messages/${messageId}/pin`,{method:"POST",body:{mutationId:crypto.randomUUID()}});}
export const getDraftSource=(id:string)=>request<{chapterId:string;revision:number;title:string;doc:Record<string,unknown>;selection:import("@novel/contracts").SelectedPassage|null;defaultStrategy:"replace_selection"|"replace_chapter"|"append_to_end"}>("/api/ai/drafts/"+id+"/source");
export function getAiDraft(draftId: string): Promise<{ draft: { id: string; revision: number; text: string; generationComplete: boolean; finishReason: string | null; baseChapterRevision: number; adoptedChapterRevision: number | null;adoptionState:import("@novel/contracts").AiAdoptionState } }> {
  return request(`/api/ai/drafts/${draftId}`);
}
